import { A } from '@adornis/base/env-info.js';
import type { Dictionary, Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import type { Filter } from 'mongodb';
import type { ChancenwerkUser } from '../master-data-management/users/entities/ChancenwerkUser.js';

@Entity()
export class ChancenwerkUserFilter extends AdornisFilter {
  static override _class = 'ChancenwerkUserFilter';

  constructor(doc: PartialEntityData<ChancenwerkUserFilter>) {
    super(doc);
  }

  // Only used to check if a filter has been changed to a new one
  @Field(() => String, { default: v => v ?? A.getGloballyUniqueID() })
  public _id!: string;

  @Field(() => [String], { default: v => v ?? [] })
  public userClass: Maybe<string[]>;

  // uses actual database _ids
  @Field(() => [String])
  public ids: Maybe<string[]>;

  // filter by role ID
  @Field(() => String)
  public roleID: Maybe<string>;

  private _buildNameCombinationsFilter(splitName: string[]) {
    const combinationsFilter: any[] = [];

    // Build any combination for n names in the name
    for (let i = 1; i < splitName.length; i++) {
      combinationsFilter.push({
        $and: [
          { vorname: { $regex: this._safeRegex(splitName.slice(0, i).join(' ')), $options: 'i' } },
          { nachname: { $regex: this._safeRegex(splitName.slice(i, splitName.length).join(' ')), $options: 'i' } },
          { vornameCleaned: { $regex: this._safeRegex(splitName.slice(0, i).join(' ')), $options: 'i' } },
          {
            nachnameCleaned: { $regex: this._safeRegex(splitName.slice(i, splitName.length).join(' ')), $options: 'i' },
          },
        ],
      });
    }

    // Intermediate so we dont throw out partial given names
    combinationsFilter.push({
      $or: [
        { vorname: { $regex: this._safeRegex(splitName.join(' ')), $options: 'i' } },
        { nachname: { $regex: this._safeRegex(splitName.join(' ')), $options: 'i' } },
        { vornameCleaned: { $regex: this._safeRegex(splitName.join(' ')), $options: 'i' } },
        { nachnameCleaned: { $regex: this._safeRegex(splitName.join(' ')), $options: 'i' } },
      ],
    });
    return combinationsFilter;
  }

  protected getNameFilter() {
    const filter = this.toObject();
    const filterName: Filter<ChancenwerkUser> = {};
    if (filter.search) {
      const searchComponents: string[] = filter.search.split(' ').filter(Boolean);
      if (searchComponents.length === 1 && searchComponents[0]) {
        filterName.$or = [
          { vorname: { $regex: this._safeRegex(searchComponents[0]), $options: 'i' } },
          { nachname: { $regex: this._safeRegex(searchComponents[0]), $options: 'i' } },
          { vornameCleaned: { $regex: this._safeRegex(searchComponents[0]), $options: 'i' } },
          { nachnameCleaned: { $regex: this._safeRegex(searchComponents[0]), $options: 'i' } },
        ];
      } else if (searchComponents.length > 1) {
        filterName.$or = this._buildNameCombinationsFilter(searchComponents);
      }
    }

    return filterName;
  }

  /**
   * Don't include aggregation filtering here
   *
   * @returns a filter that is usable without aggregations
   */
  protected getFilterArray() {
    const filter = this.toObject();
    const filterIDs = filter.ids ? { _id: { $in: filter.ids } } : {};
    const filterClass = filter.userClass?.length ? { _class: { $in: filter.userClass } } : {};
    const filterRole = filter.roleID ? { roleID: filter.roleID } : {};
    const filterName: Filter<ChancenwerkUser> = this.getNameFilter();

    return [
      // ! We don't want temp users to show up anywhere
      { _class: { $nin: ['TemporaryUser'] } },
      filterIDs,
      filterClass,
      filterRole,
      filterName,
    ];
  }

  /**
   * Hack to support filtering by cleaned names where special characters were replaced.
   * For example: é => e, so that searching for Léon and Leon both return the same results
   *
   * Example Input:
   * "$and": [
   *   { "vorname": { "$regex": "Müller" } },
   *   { "nachname": { "$regex": "Schön" } }
   * ]
   *
   * Example Output:
   * {
   *   "$and": [
   *     {
   *       "$or": [
   *         { "vorname": { "$regex": "Müller" } },
   *         { "vornameCleaned": { "$regex": "Müller" } }
   *       ]
   *     },
   *     {
   *       "$or": [
   *         { "nachname": { "$regex": "Schön" } },
   *         { "nachnameCleaned": { "$regex": "Schön" } }
   *       ]
   *     }
   *   ]
   * }
   *
   * @param query The query to fix
   * @returns The fixed query
   */
  private _fixNameQuery(query) {
    if (!this) {
      return query;
    }

    if (Array.isArray(query)) {
      return query.map(this._fixNameQuery);
    } else if (typeof query === 'object' && query !== null) {
      const transformed = {};
      for (const key in query) {
        if (key === 'vorname' || key === 'nachname') {
          transformed['$or'] = [{ [key]: query[key] }, { [`${key}Cleaned`]: query[key] }];
        } else if (key === '$and' || key === '$or') {
          transformed[key] = this._fixNameQuery(query[key]); // Recurse into logical operators
        } else {
          transformed[key] = query[key]; // Keep other fields unchanged
        }
      }
      return transformed;
    }
    return query; // Return primitive values as is
  }

  override getMongoFilter(): Dictionary<any> {
    const filters = this.getFilterArray();
    const extendedFilter = super.getMongoFilter();
    if (extendedFilter.$and?.length) {
      filters.push(this._fixNameQuery(extendedFilter));
    }
    if (!filters.length) return {};
    return { $and: filters };
  }
}
